import useCopy from '@react-hook/copy';
import { useDebounce } from '@react-hook/debounce';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { isIOS } from 'react-device-detect';
import useWebShare from 'react-use-web-share';

import { AccessPointFragment as AccessPoint } from '../../apollo';
import { useAcceptedTermsAt, useAnonymousUserId } from '../../hooks';

import { Button, Modal } from '..';
import TermsOfUseModal from '../TermsOfUseModal';
import styles from './styles';

interface RequestAccessButtonsProps {
  accessPoint: Pick<AccessPoint, 'accessPointId' | 'name'>;
  showButtons: boolean;
  disableRequestButton: boolean;
  onRequested: () => void;
}

const RequestAccessButtons: React.FC<RequestAccessButtonsProps> = (props) => {
  const [acceptedTermsAt, setAcceptedTermsAt] = useAcceptedTermsAt();
  const anonymousUserId = useAnonymousUserId();
  const { loading, isSupported, share } = useWebShare();

  const deepLink = `https://stratisiot.com/mobile/entry?propertyId=${props.accessPoint.accessPointId}&anonymousUserId=${anonymousUserId}`;
  const { copied, copy, reset } = useCopy(deepLink);

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [deepLinkCopied, setDeepLinkCopied] = useDebounce(false, 2000);

  useEffect(() => {
    if (deepLinkCopied) {
      reset();
      setDeepLinkCopied(false);
    }
  }, [deepLinkCopied, reset, setDeepLinkCopied]);

  const handleRequestAccess = () => {
    if (isSupported) {
      share({
        title: 'Request Entry Access',
        text: `Hey, I'm at ${props.accessPoint.name}. Please approve my access request by tapping the link below:\n\n`,
        url: deepLink,
      });
      props.onRequested();
    } else {
      setShowCopyModal(true);
      props.onRequested();
    }
  };

  return (
    <>
      <TermsOfUseModal
        isOpen={showTermsModal}
        closeModal={() => setShowTermsModal(false)}
        loading={loading}
        onAcceptTerms={async () => {
          setShowTermsModal(false);
          setAcceptedTermsAt(moment.utc(new Date()).toISOString());
          handleRequestAccess();
        }}
      />
      {/* WEB-SHARE NOT SUPPORTED COPY MODAL */}
      <Modal
        show={showCopyModal}
        showAtBottom
        message={(
          <div>
            Request temporary access from a resident by sending them the link below.
            <div style={{ marginTop: 12 }}>
              <a href={deepLink}>
                {deepLink}
              </a>
            </div>
          </div>
        )}
        buttons={[
          {
            text: copied || deepLinkCopied ? 'Copied!' : 'Copy Link',
            type: 'outline',
            size: 'large',
            onClick: () => {
              copy();
              setDeepLinkCopied(true);
            },
          },
          {
            text: 'Continue',
            type: 'primary',
            size: 'large',
            onClick: () => {
              setShowCopyModal(false);
            },
          },
        ]}
      />
      {props.showButtons && (
        <div className={isIOS ? styles.iOSButtonsWrapper : styles.buttonsWrapper}>
          <Button
            className={styles.button}
            type="primary"
            size="large"
            expand
            disabled={props.disableRequestButton}
            loading={loading}
            onClick={() => {
              if (props.disableRequestButton) return;
              if (!acceptedTermsAt) {
                setShowTermsModal(true);
              } else {
                handleRequestAccess();
              }
            }}
          >
            Request Visitor Access
        </Button>
          {/* For now hide this button as it doesn't have any intended functionality yet */}
          {/* <Button
            className={styles.button}
            type='outline'
            size='large'
            expand
            onClick={() => { }}
          >
            I am a Vendor
          </Button> */}
        </div>
      )}
    </>
  );
};

export default RequestAccessButtons;
